import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { useApi } from "../hooks";
import { useAuth } from "../auth";
import { stageLabel } from "../crm";
import { DRIP_STATUS_LABEL, triggerSummary, whoSentence, type Drip, type DripDefaults, type DripRow } from "../drips";
import DripBuilder, { DomainWait } from "../components/crm/DripBuilder";
import DripSettingsDialog from "../components/crm/DripSettingsDialog";
import type { CrmForm } from "../components/crm/types";
import { EmptyState, ErrorState, PageHeader, PageSkeleton } from "../components/Ui";
import "../polish/crm-drips.css";

/** Drip campaigns: the list, and (with ?drip=<id>) one drip's page, laid out like Magus's campaigns. */
export default function CrmDrips() {
  const [params, setParams] = useSearchParams();
  const dripId = Number(params.get("drip")) || null;
  const openDrip = (id: number | null, extra?: Record<string, string>) => setParams((cur) => {
    const next = new URLSearchParams(cur);
    if (id) next.set("drip", String(id)); else next.delete("drip");
    for (const k of ["tab", "contact", "edit"]) next.delete(k);
    for (const [k, v] of Object.entries(extra || {})) next.set(k, v);
    return next;
  });
  if (dripId) return <DripBuilder key={dripId} id={dripId} onBack={() => openDrip(null)} />;
  return <DripList onOpen={openDrip} />;
}

function DripList({ onOpen }: { onOpen: (id: number, extra?: Record<string, string>) => void }) {
  const list = useApi<{ drips: DripRow[]; sending: { waiting_on_domain: boolean }; defaults?: DripDefaults }>("/api/crm/drips");
  const forms = useApi<CrmForm[]>("/api/crm/forms");
  const { user } = useAuth();
  const canWrite = user?.role === "superadmin" || user?.role === "exec";
  const [creating, setCreating] = useState(false);
  const rows = list.data?.drips || [];
  const formName = (id: number) => forms.data?.find((f) => f.id === id)?.name || "a form";
  const create = () => setCreating(true);
  const newDrip = canWrite && <button className="button" onClick={create}><Plus aria-hidden="true" />New drip</button>;

  return (
    <div className="crm-drips drip-page">
      <PageHeader title="Drips" description="Short email series that keep leads warm until they are ready to talk." actions={newDrip} />
      {list.data?.sending.waiting_on_domain
        ? <DomainWait />
        : <div className="drip-strip"><span className="drip-dot" aria-hidden="true" /><span>Emails go out 9:00 AM to 5:00 PM ET from your No Limits address, and stop when someone books or unsubscribes.</span></div>}

      {/* A table like Forms: one row per drip, the whole row opens it. */}
      {list.loading && !list.data ? <PageSkeleton cards={0} />
        : list.error ? <ErrorState message={list.error} onRetry={list.reload} />
        : !rows.length ? <section className="panel"><EmptyState title="No drips yet" description="Write a few short emails, choose who gets them, and NOLA sends one every few days until they book a call." action={newDrip || undefined} /></section>
        : (
          <section className="panel table-wrap">
            <table className="module-table crm-table crm-forms-table crm-drips-table">
              <thead><tr><th>Drip</th><th>Who gets it</th><th>Status</th><th className="num">Active</th><th className="num">Sent</th><th className="num">Finished</th><th className="num">Emails</th></tr></thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.id} className="is-clickable" onClick={() => onOpen(d.id)}>
                    <td data-label="Drip"><button type="button" className="crm-row-open" onClick={(e) => { e.stopPropagation(); onOpen(d.id); }}>{d.name}</button></td>
                    <td data-label="Who gets it" className="crm-drip-who" title={whoSentence(d.triggers, formName, stageLabel)}>{triggerSummary(d.triggers, formName, stageLabel)}</td>
                    <td data-label="Status"><span className={`crm-status ${d.status}`}>{DRIP_STATUS_LABEL[d.status]}</span></td>
                    <td data-label="Active" className="num">{d.active}</td>
                    <td data-label="Sent" className="num">{d.sent}</td>
                    <td data-label="Finished" className="num">{d.finished}</td>
                    <td data-label="Emails" className="num">{d.steps}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="crm-table-foot">
              <span>{rows.length} {rows.length === 1 ? "drip" : "drips"}</span>
            </div>
          </section>
        )}
      {creating && (
        <DripSettingsDialog drip={null} forms={forms.data || []} defaults={list.data?.defaults} onClose={() => setCreating(false)}
          onSaved={(d: Drip) => {
            setCreating(false);
            // The first email opens ready to write only when the dialog left it empty.
            const firstEmpty = !d.steps?.[0]?.subject || !d.steps?.[0]?.body;
            onOpen(d.id, { ...(firstEmpty ? { edit: "0" } : {}), ...(d.status === "live" ? { started: String(d.started || 0) } : {}) });
          }} />
      )}
    </div>
  );
}

